import { useCallback, useState } from 'react';
import { container } from '@infrastructure/container';
import { useAuth } from './useAuth';
import { useAuthStore } from '../../application/store/AuthStore';
import { establishSessionForCurrentAppwriteUser } from '../../application/auth/establishSession';
import {
  clearImpersonationTarget,
  getImpersonationTargetUserId,
  getOperatorCompanyId,
  setImpersonationTarget,
} from '../../application/auth/impersonation';

export function useImpersonation() {
  const { user, login } = useAuth();
  const [detectedImpersonator, setDetectedImpersonator] = useState(false);

  const isImpersonating = Boolean(user?.impersonatorUserId || getImpersonationTargetUserId());
  const canImpersonate = Boolean(user?.impersonator || detectedImpersonator || isImpersonating);

  const detectImpersonatorCapability = useCallback(async () => {
    if (user?.impersonator || detectedImpersonator) {
      return true;
    }

    const canImpersonateOthers = await container.authRepository.hasImpersonatorAccess();
    if (canImpersonateOthers) {
      setDetectedImpersonator(true);
    }
    return canImpersonateOthers;
  }, [detectedImpersonator, user?.impersonator]);

  const startImpersonation = useCallback(async (targetUserId: string) => {
    const allowed = user?.impersonator || detectedImpersonator || await detectImpersonatorCapability();
    if (!allowed) {
      throw new Error('Current user cannot impersonate others');
    }

    const operatorCompanyId = useAuthStore.getState().companyId;
    setImpersonationTarget(targetUserId, operatorCompanyId);

    try {
      await establishSessionForCurrentAppwriteUser(login);
      const impersonatedUser = await container.authRepository.getCurrentUser();
      if (
        impersonatedUser.status === 'error'
        || impersonatedUser.data?.id !== targetUserId
      ) {
        throw new Error('Impersonation was not accepted by Appwrite. Enable the impersonator capability for this account.');
      }
    } catch (err) {
      clearImpersonationTarget();
      try {
        await establishSessionForCurrentAppwriteUser(login);
      } catch {
        await useAuthStore.getState().refreshJwt();
      }
      throw err;
    }
  }, [detectedImpersonator, detectImpersonatorCapability, login, user?.impersonator]);

  const stopImpersonation = useCallback(async () => {
    const operatorCompanyId = getOperatorCompanyId();
    clearImpersonationTarget();

    const jwt = await useAuthStore.getState().refreshJwt();
    if (!jwt) {
      throw new Error('Failed to refresh session');
    }

    const userResponse = await container.authRepository.getCurrentUser();
    if (userResponse.status === 'error' || !userResponse.data) {
      throw new Error('Failed to load user');
    }

    if (operatorCompanyId) {
      const companyResponse = await container.companyRepository.getCompany(operatorCompanyId);
      if (companyResponse.status === 'success' && companyResponse.data) {
        await login(userResponse.data, jwt, companyResponse.data);
        return;
      }
    }

    await establishSessionForCurrentAppwriteUser(login);
  }, [login]);

  return {
    isImpersonating,
    canImpersonate,
    startImpersonation,
    stopImpersonation,
    detectImpersonatorCapability,
  };
}
